/**
 * Página pública para compartir logros de ProPoints
 * 
 * URL: /share/partido/:partidoId/futbolista/:futbolistaId
 * 
 * Muestra los logros obtenidos por un futbolista en un partido específico
 * con diseño optimizado para compartir en redes sociales
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import '../../styles/share-logros.css';
import { formatFechaPartido } from '../../utils/fechaUtils';

const ShareLogros = () => {
  const { partidoId, futbolistaId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLogros = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `/api/postgres/share/partido/${partidoId}/futbolista/${futbolistaId}`
        );

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setData(result.data);
          } else {
            setError(result.message);
          }
        } else {
          setError('No se pudieron cargar los logros');
        }
      } catch (err) {
        console.error('Error cargando logros:', err);
        setError('Error al cargar los logros');
      } finally {
        setLoading(false);
      }
    };

    fetchLogros();
  }, [partidoId, futbolistaId]);

  const getLogroImageUrl = (logroNombre, genero) => {
    const baseUrl = window.location.origin;
    const isFemenino = genero === 'F';

    // Normalizar el nombre del logro (de "El Desequilibrante" a "el_desequilibrante")
    const normalizedNombre = logroNombre
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Eliminar acentos
      .replace(/\s+/g, '_'); // Reemplazar espacios con guiones bajos

    const imageMapMasculino = {
      'participacion': '/images/jugadorcampo-1x.jpeg',
      'victoria': '/images/jugadorcampo-1x.jpeg',
      'destacado': '/images/jugadorcampo-1x.jpeg',
      'el_jugon': '/images/eljugon-1x.jpeg',
      'el_cerrojo': '/images/cerrojo-1x.jpeg',
      'el_desequilibrante': '/images/desequilibrante-1x.jpeg',
      'el_lider': '/images/lider-1x.jpeg',
      'el_mejor_companero': '/images/mejorcompanero-1x.jpeg'
    };

    const imageMapFemenino = {
      'participacion': '/images/la_jugadorcampo.png',
      'victoria': '/images/la_jugadorcampo.png',
      'destacado': '/images/la_jugadorcampo.png',
      'el_jugon': '/images/la_eljugon.png',
      'el_cerrojo': '/images/la_cerrojo.png',
      'el_desequilibrante': '/images/la_desequilibrante.png',
      'el_lider': '/images/la_lider.png',
      'el_mejor_companero': '/images/la_mejorcompanero.png'
    };

    const imageMap = isFemenino ? imageMapFemenino : imageMapMasculino;
    const defaultImage = isFemenino ? '/images/la_jugadorcampo.png' : '/images/jugadorcampo-1x.jpeg';

    const imagePath = imageMap[normalizedNombre] || defaultImage;
    return `${baseUrl}${imagePath}`;
  };

  if (loading) {
    return (
      <div className="share-logros-container">
        <div className="share-logros-loading">
          <div className="spinner"></div>
          <p>Cargando logros...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="share-logros-container">
        <div className="share-logros-error">
          <span className="material-icons">error_outline</span>
          <h2>No se pudieron cargar los logros</h2>
          <p>{error || 'Intenta nuevamente más tarde'}</p>
        </div>
      </div>
    );
  }

  const { futbolista, partido, logros, total_puntos } = data;

  // Preparar datos para meta tags
  const pageTitle = `${futbolista.nombre} - ${total_puntos} ProPoints | Liga ProKids`;
  const pageDescription = `¡${futbolista.nombre} ha conseguido ${logros.length} logro${logros.length !== 1 ? 's' : ''} en Liga ProKids! Total: ${total_puntos} ProPoints`;
  const pageUrl = window.location.href;
  const pageImage = `${window.location.origin}/images/logo.png`;
  const logrosText = logros.map(l => l.nombre).join(', ');

  return (
    <div className="share-logros-container">
      {/* Meta tags para redes sociales */}
      <Helmet>
        {/* Título de la página */}
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />

        {/* Open Graph (Facebook, WhatsApp, LinkedIn) */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`🏆 ${futbolista.nombre} - ${total_puntos} ProPoints`} />
        <meta property="og:description" content={`¡He conseguido ${logros.length} logro${logros.length !== 1 ? 's' : ''} en Liga ProKids! ${logrosText}`} />
        <meta property="og:image" content={pageImage} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content="Liga ProKids" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`🏆 ${futbolista.nombre} - ${total_puntos} ProPoints`} />
        <meta name="twitter:description" content={`¡He conseguido ${logros.length} logro${logros.length !== 1 ? 's' : ''} en Liga ProKids!`} />
        <meta name="twitter:image" content={pageImage} />

        {/* WhatsApp específico */}
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
      </Helmet>

      <div className="share-logros-card">
        {/* Header con logo */}
        <div className="share-logros-header">
          <img 
            src="/images/logo.png" 
            alt="Liga ProKids" 
            className="share-logros-logo"
          />
          <h1>Liga ProKids®</h1>
          <p className="share-logros-subtitle">Sistema ProPoints™</p>
        </div>

        {/* Información del futbolista */}
        <div className="share-logros-player">
          <div className="share-logros-player-avatar">
            {futbolista.foto ? (
              <img src={futbolista.foto} alt={futbolista.nombre} />
            ) : (
              <span className="material-icons">person</span>
            )}
          </div>
          <h2>{futbolista.nombre}</h2>
          <p className="share-logros-team">{futbolista.equipo}</p>
        </div>

        {/* Información del partido */}
        <div className="share-logros-match-info">
          <p>
            <strong>{partido.torneo}</strong>
          </p>
          <p>Jornada {partido.jornada_numero} • {formatFechaPartido(partido.fecha_partido, partido.jornada_fecha, 'corto')}</p>
          <p>{partido.sede}</p>
        </div>

        {/* Logros obtenidos */}
        <div className="share-logros-achievements">
          <h3>🏆 Logros Obtenidos</h3>

          {/* Grid de cartas de logros */}
          <div className="share-logros-grid">
            {logros.map((logro, index) => (
              <div key={index} className="share-logro-card-grid">
                <img
                  src={getLogroImageUrl(logro.logro_nombre, futbolista.genero)}
                  alt={logro.logro_descripcion}
                  className="share-logro-card-image"
                />
                <div className="share-logro-card-overlay">
                  <span className="share-logro-card-points">+{logro.logro_puntos} PP</span>
                </div>
              </div>
            ))}
          </div>

          {/* Total de ProPoints */}
          <div className="share-logros-total">
            <span className="share-logros-total-label">Total ProPoints</span>
            <span className="share-logros-total-value">{total_puntos} PP</span>
          </div>
        </div>

        {/* Footer */}
        <div className="share-logros-footer">
          <p>¡Únete a la Liga ProKids y gana ProPoints™!</p>
          <a
            href="https://ligaprokids.com"
            target="_blank"
            rel="noopener noreferrer"
            className="share-logros-cta"
          >
            Más información
          </a>
        </div>
      </div>
    </div>
  );
};

export default ShareLogros;
